import Auth from '../api/auth';
import { emitter } from 'shared/helpers/mitt';
import { redirectForAceleresSessionEnd } from '../store/utils/api';
import {
  accessProfileResponseMessage,
  isAccessProfileMessage,
  showAccessProfileNotice,
} from './accessProfileNotice';

const parseErrorCode = error => Promise.reject(error);

// Aceleres: retry com backoff para leituras que falharam por 5xx/timeout/rede.
// Restrito a metodos idempotentes — POST/PATCH/DELETE nunca sao repetidos, pra
// nao duplicar escrita. Opt-out por request com `noRetry: true` no config.
const RETRYABLE_METHODS = ['get', 'head', 'options'];
const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 700;

const isRetryableFailure = (axios, error) => {
  if (axios.isCancel?.(error)) return false;
  const config = error?.config;
  if (!config || config.noRetry) return false;
  const method = String(config.method || '').toLowerCase();
  if (!RETRYABLE_METHODS.includes(method)) return false;
  const status = error?.response?.status;
  // Sem response = timeout ou rede caiu. Com response, so 5xx merece retry.
  if (status === undefined) return true;
  return status >= 500 && status < 600;
};

export default axios => {
  const { apiHost = '' } = window.chatwootConfig || {};
  const wootApi = axios.create({ baseURL: `${apiHost}/` });
  // Add Auth Headers to requests if logged in
  if (Auth.hasAuthCookie()) {
    const {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    } = Auth.getAuthData();
    Object.assign(wootApi.defaults.headers.common, {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    });
  }
  wootApi.interceptors.request.use(config => {
    if (window.aceleresSessionTerminationStarted) {
      return Promise.reject(new axios.Cancel('Aceleres session ended'));
    }

    return config;
  });
  // Response parsing interceptor
  wootApi.interceptors.response.use(
    response => response,
    error => {
      const endedByAceleres =
        error?.response?.status === 401 &&
        error?.response?.data?.error === 'aceleres_session_ended' &&
        error?.response?.data?.reason === 'sso_session_revoked';

      if (endedByAceleres) {
        redirectForAceleresSessionEnd();
        // The auth store has a legacy generic-401 fallback. Prevent that
        // fallback from replacing the precise SSO redirect above.
        if (error.response?.data) {
          error.response.data.error = 'aceleres_session_ended_handled';
        }
      }

      const profileDenied =
        error?.response?.status === 403 &&
        ['access_profile_denied', 'permission_denied'].includes(
          error.response?.data?.code
        ) &&
        /\/api\/v1\/accounts\/\d+\//.test(error.config?.url || '');
      if (profileDenied) {
        const message = accessProfileResponseMessage(error.response.data);
        error.response.data.message = message;
        error.response.data.error = message;
        error.message = message;
        const method = String(error.config?.method || 'get').toLowerCase();
        if (
          !RETRYABLE_METHODS.includes(method) ||
          error.config?.accessProfileFeedback === 'action'
        ) {
          showAccessProfileNotice({ message });
        }
        emitter.emit('aceleres.access_profile.refresh');
      }

      const friendlyDenial =
        error?.response?.data?.message || error?.response?.data?.error;
      if (
        [401, 403].includes(error?.response?.status) &&
        isAccessProfileMessage(friendlyDenial)
      ) {
        error.message = friendlyDenial;
      }

      if (
        !endedByAceleres &&
        !window.aceleresSessionTerminationStarted &&
        isRetryableFailure(axios, error)
      ) {
        const config = error.config;
        config.__aceleresRetryCount = (config.__aceleresRetryCount || 0) + 1;
        if (config.__aceleresRetryCount <= MAX_RETRIES) {
          const delay =
            RETRY_BASE_DELAY_MS * 2 ** (config.__aceleresRetryCount - 1);
          return new Promise(resolve => {
            setTimeout(resolve, delay);
          }).then(() => wootApi(config));
        }
      }

      return parseErrorCode(error);
    }
  );
  return wootApi;
};
